import { CreateKeyframeData, UpdateKeyframeData, KeyframeDatabaseProvider } from '@/modules/keyframes/types';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { listAll, listAllIn } from '@/infrastructure/api/v1/lists';
import { keyframeToLegacy, keyframeCreateBody, keyframePatchBody, type LegacyKeyframe } from '@/infrastructure/api/v1/mappers/keyframes';

import type { WriteScope } from '@/modules/undoRedo';
const byTime = (a: LegacyKeyframe, b: LegacyKeyframe) =>
  a.keyframe_time - b.keyframe_time || a.created_at.localeCompare(b.created_at);

export class KeyframeAdapter implements KeyframeDatabaseProvider {
  async fetchKeyframesForAsset(assetInstanceId: string): Promise<any[]> {
    try {
      const { data, error } = await listAll(page => v1.GET('/api/v1/keyframes', {
        params: { query: { assetInstanceUniqueId: [assetInstanceId], ...page } },
      }));
      if (error) throw error;
      return data.map(keyframeToLegacy);
    } catch (error) {
      console.error('Error fetching keyframes for asset:', error);
      throw new Error((error as Error).message, { cause: error });
    }
  }

  async fetchKeyframesForAssets(assetInstanceIds: string[]): Promise<any[]> {
    if (assetInstanceIds.length === 0) return [];
    try {
      const { data, error } = await listAllIn(assetInstanceIds, (assetInstanceId, page) => v1.GET('/api/v1/keyframes', {
        params: { query: { assetInstanceUniqueId: assetInstanceId, ...page } },
      }));
      if (error) throw error;
      return data.map(keyframeToLegacy).sort(byTime);
    } catch (error) {
      console.error('Error fetching keyframes for assets:', error);
      throw new Error((error as Error).message, { cause: error });
    }
  }

  async fetchKeyframeRow(keyframeId: string): Promise<any> {
    try {
      const { data, error } = await toResult(v1.GET('/api/v1/keyframes/{uniqueId}', { params: { path: { uniqueId: keyframeId } } }));
      if (error) throw error;
      return keyframeToLegacy(data);
    } catch (error) {
      console.error('Error fetching keyframe row:', error);
      throw new Error((error as Error).message, { cause: error });
    }
  }

  async addKeyframeRow(newKeyframe: CreateKeyframeData, scope?: WriteScope): Promise<any> {
    try {
      // The id is generated here when the caller leaves it to the database, so a retry stays idempotent
      const body = keyframeCreateBody({ ...newKeyframe, keyframe_id: newKeyframe.keyframe_id ?? crypto.randomUUID() });
      const { data, error } = await toResult(v1.POST('/api/v1/keyframes', { body, headers: scope?.headers }));
      if (error) throw error;

      const rows = [keyframeToLegacy(data)];
      console.log('Keyframe added successfully:', rows);
      return rows;
    } catch (error) {
      console.error('Error adding keyframe row:', error);
      throw new Error((error as Error).message, { cause: error });
    }
  }

  async updateKeyframeRow(keyframeId: string, updateData: UpdateKeyframeData, scope?: WriteScope): Promise<any> {
    try {
      const { data, error } = await toResult(v1.PATCH('/api/v1/keyframes/{uniqueId}', {
        params: { path: { uniqueId: keyframeId } },
        body: keyframePatchBody(updateData),
        headers: scope?.headers,
      }));
      if (error) throw error;

      const rows = [keyframeToLegacy(data)];
      console.log('Keyframe updated successfully:', rows);
      return rows;
    } catch (error) {
      console.error('Error updating keyframe row:', error);
      throw new Error((error as Error).message, { cause: error });
    }
  }

  async deleteKeyframeRow(keyframeId: string, scope?: WriteScope): Promise<void> {
    try {
      const { error } = await toResult(v1.DELETE('/api/v1/keyframes/{uniqueId}', { params: { path: { uniqueId: keyframeId } }, headers: scope?.headers }));
      // A keyframe already gone is deleted, as the generic route's filtered delete treated it
      if (error && error.status !== 404) throw error;

      console.log('Keyframe deleted successfully');
    } catch (error) {
      console.error('Error deleting keyframe row:', error);
      throw new Error((error as Error).message, { cause: error });
    }
  }

}
